import React, { useState, useEffect } from 'react';
import { API_URL } from '../../config/api.ts';
import {
    Server,
    Database,
    Image,
    Trash2,
    RefreshCw,
    CheckCircle,
    AlertCircle,
    Clock,
    Play,
    Activity
} from 'lucide-react';

interface MigrationStatus {
    users: {
        base64: number;
        cloudinary: number;
        external: number;
        needsMigration: number;
    };
    characters: {
        avatars: {
            base64: number;
            cloudinary: number;
        };
        backgrounds: {
            base64: number;
            cloudinary: number;
        };
    };
}

const AdminSystem: React.FC = () => {
    const [migrationStatus, setMigrationStatus] = useState<MigrationStatus | null>(null);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState<string | null>(null);
    const [actionResult, setActionResult] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

    const fetchMigrationStatus = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${API_URL}/admin/migration-status`, {
                credentials: 'include'
            });
            const data = await response.json();

            if (data.success) {
                setMigrationStatus(data.migration);
            }
        } catch (error) {
            console.error('Error fetching migration status:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchMigrationStatus();
    }, []);

    const handleRunMigration = async (dryRun: boolean) => {
        try {
            setActionLoading('migration');
            setActionResult(null);

            const response = await fetch(`${API_URL}/upload/migrate/all`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ dryRun })
            });
            const data = await response.json();

            if (data.success) {
                setActionResult({
                    type: 'success',
                    message: dryRun
                        ? `Preview: ${data.report.users.migrated} usuarios y ${data.report.characters.migrated} personajes serían migrados`
                        : `Migración completa: ${data.report.users.migrated} usuarios y ${data.report.characters.migrated} personajes migrados`
                });
                fetchMigrationStatus();
            } else {
                setActionResult({ type: 'error', message: data.message || 'Error en migración' });
            }
        } catch (error) {
            setActionResult({ type: 'error', message: 'Error ejecutando migración' });
        } finally {
            setActionLoading(null);
        }
    };

    const handleProcessDeletions = async () => {
        try {
            setActionLoading('deletions');
            setActionResult(null);

            const response = await fetch(`${API_URL}/admin/cleanup/scheduled-deletions`, {
                method: 'POST',
                credentials: 'include'
            });
            const data = await response.json();

            if (data.success) {
                setActionResult({
                    type: 'success',
                    message: `Procesadas ${data.processed} eliminaciones programadas`
                });
            } else {
                setActionResult({ type: 'error', message: data.message || 'Error' });
            }
        } catch (error) {
            setActionResult({ type: 'error', message: 'Error procesando eliminaciones' });
        } finally {
            setActionLoading(null);
        }
    };

    return (
        <div>
            {/* Header */}
            <div style={{ marginBottom: '32px' }}>
                <h1 style={{ color: 'white', fontSize: '28px', fontWeight: '700', marginBottom: '8px' }}>
                    Sistema
                </h1>
                <p style={{ color: 'rgba(255,255,255,0.5)' }}>
                    Herramientas de mantenimiento y estado del sistema
                </p>
            </div>

            {/* Action Result Alert */}
            {actionResult && (
                <div style={{
                    padding: '16px 20px',
                    borderRadius: '12px',
                    marginBottom: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    background: actionResult.type === 'success'
                        ? 'rgba(78, 205, 196, 0.15)'
                        : 'rgba(255, 107, 107, 0.15)',
                    border: `1px solid ${actionResult.type === 'success' ? 'rgba(78, 205, 196, 0.3)' : 'rgba(255, 107, 107, 0.3)'}`
                }}>
                    {actionResult.type === 'success' ? (
                        <CheckCircle size={20} style={{ color: '#4ecdc4' }} />
                    ) : (
                        <AlertCircle size={20} style={{ color: '#ff6b6b' }} />
                    )}
                    <span style={{ color: 'white' }}>{actionResult.message}</span>
                </div>
            )}

            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
                gap: '24px'
            }}>
                {/* Migration Status Card */}
                <div style={{
                    background: 'rgba(255,255,255,0.05)',
                    backdropFilter: 'blur(20px)',
                    borderRadius: '20px',
                    padding: '24px',
                    border: '1px solid rgba(255,255,255,0.1)'
                }}>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '20px'
                    }}>
                        <h3 style={{
                            color: 'white',
                            fontSize: '18px',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            margin: 0
                        }}>
                            <Image size={20} style={{ color: '#4ecdc4' }} />
                            Estado de Migración
                        </h3>
                        <button
                            onClick={fetchMigrationStatus}
                            style={{
                                padding: '8px',
                                background: 'rgba(255,255,255,0.1)',
                                border: 'none',
                                borderRadius: '8px',
                                color: 'rgba(255,255,255,0.6)',
                                cursor: 'pointer'
                            }}
                        >
                            <RefreshCw size={16} />
                        </button>
                    </div>

                    {loading ? (
                        <div style={{ textAlign: 'center', padding: '20px', color: 'rgba(255,255,255,0.5)' }}>
                            <RefreshCw size={24} style={{ animation: 'spin 1s linear infinite' }} />
                        </div>
                    ) : migrationStatus ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            {/* Users */}
                            <div>
                                <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px', marginBottom: '8px' }}>
                                    Avatares de Usuario
                                </p>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <div style={{
                                        flex: 1,
                                        padding: '12px',
                                        background: 'rgba(78, 205, 196, 0.1)',
                                        borderRadius: '10px',
                                        textAlign: 'center'
                                    }}>
                                        <p style={{ color: '#4ecdc4', fontSize: '20px', fontWeight: '700' }}>
                                            {migrationStatus.users.cloudinary}
                                        </p>
                                        <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '11px' }}>Cloudinary</p>
                                    </div>
                                    <div style={{
                                        flex: 1,
                                        padding: '12px',
                                        background: migrationStatus.users.needsMigration > 0
                                            ? 'rgba(255, 217, 61, 0.1)'
                                            : 'rgba(78, 205, 196, 0.1)',
                                        borderRadius: '10px',
                                        textAlign: 'center'
                                    }}>
                                        <p style={{
                                            color: migrationStatus.users.needsMigration > 0 ? '#ffd93d' : '#4ecdc4',
                                            fontSize: '20px',
                                            fontWeight: '700'
                                        }}>
                                            {migrationStatus.users.needsMigration}
                                        </p>
                                        <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '11px' }}>Pendientes</p>
                                    </div>
                                </div>
                            </div>

                            {/* Characters */}
                            <div>
                                <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px', marginBottom: '8px' }}>
                                    Imágenes de Personajes (Avatares)
                                </p>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <div style={{
                                        flex: 1,
                                        padding: '12px',
                                        background: 'rgba(78, 205, 196, 0.1)',
                                        borderRadius: '10px',
                                        textAlign: 'center'
                                    }}>
                                        <p style={{ color: '#4ecdc4', fontSize: '20px', fontWeight: '700' }}>
                                            {migrationStatus.characters.avatars.cloudinary}
                                        </p>
                                        <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '11px' }}>Cloudinary</p>
                                    </div>
                                    <div style={{
                                        flex: 1,
                                        padding: '12px',
                                        background: migrationStatus.characters.avatars.base64 > 0
                                            ? 'rgba(255, 217, 61, 0.1)'
                                            : 'rgba(78, 205, 196, 0.1)',
                                        borderRadius: '10px',
                                        textAlign: 'center'
                                    }}>
                                        <p style={{
                                            color: migrationStatus.characters.avatars.base64 > 0 ? '#ffd93d' : '#4ecdc4',
                                            fontSize: '20px',
                                            fontWeight: '700'
                                        }}>
                                            {migrationStatus.characters.avatars.base64}
                                        </p>
                                        <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '11px' }}>Base64</p>
                                    </div>
                                </div>
                            </div>

                            {/* Migration Actions */}
                            <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
                                <button
                                    onClick={() => handleRunMigration(true)}
                                    disabled={actionLoading !== null}
                                    style={{
                                        flex: 1,
                                        padding: '12px',
                                        background: 'rgba(255,255,255,0.1)',
                                        border: '1px solid rgba(255,255,255,0.2)',
                                        borderRadius: '12px',
                                        color: 'white',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '8px',
                                        fontSize: '14px'
                                    }}
                                >
                                    <Activity size={16} />
                                    Preview
                                </button>
                                <button
                                    onClick={() => handleRunMigration(false)}
                                    disabled={actionLoading !== null}
                                    style={{
                                        flex: 1,
                                        padding: '12px',
                                        background: 'linear-gradient(135deg, rgba(78, 205, 196, 0.3), rgba(68, 160, 141, 0.3))',
                                        border: '1px solid rgba(78, 205, 196, 0.4)',
                                        borderRadius: '12px',
                                        color: '#4ecdc4',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '8px',
                                        fontSize: '14px',
                                        fontWeight: '600'
                                    }}
                                >
                                    {actionLoading === 'migration' ? (
                                        <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} />
                                    ) : (
                                        <Play size={16} />
                                    )}
                                    Migrar
                                </button>
                            </div>
                        </div>
                    ) : (
                        <p style={{ color: 'rgba(255,255,255,0.5)' }}>Error cargando estado</p>
                    )}
                </div>

                {/* System Maintenance Card */}
                <div style={{
                    background: 'rgba(255,255,255,0.05)',
                    backdropFilter: 'blur(20px)',
                    borderRadius: '20px',
                    padding: '24px',
                    border: '1px solid rgba(255,255,255,0.1)'
                }}>
                    <h3 style={{
                        color: 'white',
                        fontSize: '18px',
                        fontWeight: '600',
                        marginBottom: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px'
                    }}>
                        <Server size={20} style={{ color: '#ff6b6b' }} />
                        Mantenimiento
                    </h3>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        {/* Scheduled Deletions */}
                        <div style={{
                            padding: '16px',
                            background: 'rgba(0,0,0,0.2)',
                            borderRadius: '12px'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                                <Clock size={20} style={{ color: '#ffd93d' }} />
                                <div>
                                    <p style={{ color: 'white', fontWeight: '600' }}>Eliminaciones Programadas</p>
                                    <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px' }}>
                                        Procesar cuentas marcadas para eliminación
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={handleProcessDeletions}
                                disabled={actionLoading !== null}
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    background: 'rgba(255, 217, 61, 0.2)',
                                    border: '1px solid rgba(255, 217, 61, 0.3)',
                                    borderRadius: '10px',
                                    color: '#ffd93d',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '8px'
                                }}
                            >
                                {actionLoading === 'deletions' ? (
                                    <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} />
                                ) : (
                                    <Trash2 size={16} />
                                )}
                                Procesar Ahora
                            </button>
                        </div>

                        {/* System Info */}
                        <div style={{
                            padding: '16px',
                            background: 'rgba(0,0,0,0.2)',
                            borderRadius: '12px'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                                <Database size={20} style={{ color: '#4ecdc4' }} />
                                <div>
                                    <p style={{ color: 'white', fontWeight: '600' }}>Cloudinary</p>
                                    <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px' }}>
                                        Almacenamiento de imágenes activo
                                    </p>
                                </div>
                            </div>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '8px 12px',
                                background: 'rgba(78, 205, 196, 0.1)',
                                borderRadius: '8px'
                            }}>
                                <CheckCircle size={16} style={{ color: '#4ecdc4' }} />
                                <span style={{ color: '#4ecdc4', fontSize: '13px' }}>Conectado</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
        </div>
    );
};

export default AdminSystem;
